import { http, HttpResponse } from 'msw';

import type { TaxPayment } from 'modules/Tax/api/listTaxPayments';
import type { QTE } from 'modules/Tax/types';
import type { QTEDisplayState } from 'services/tax/queries/useQTEDisplayState';

/**
 * Storybook merges a story's `msw.handlers` into the defaults **by key**, so an override under a
 * different key doesn't replace the global handler — both stay registered and the global one wins,
 * silently. The dupe-key guard in `defaults.ts` can't catch that: it only detects the same key used
 * by two handler groups. Import these when overriding either endpoint so the keys can't drift.
 */
export const QTE_HANDLER_KEYS = {
    memberTaxPayments: 'qteMemberTaxPayments',
    memberQtes: 'qteMemberQtes',
    taxProfile: 'qteTaxProfile',
    taxJurisdictionInfo: 'qteTaxJurisdictionInfo',
    displayState: 'qteDisplayState',
} as const;

export default {
    [QTE_HANDLER_KEYS.memberTaxPayments]: http.get('*/qte/member-tax-payments/', () =>
        HttpResponse.json([] satisfies TaxPayment[])
    ),
    [QTE_HANDLER_KEYS.memberQtes]: http.get('*/qte/member-qte/', () => HttpResponse.json([] satisfies QTE[])),
    // California + Single, i.e. remittance-eligible, so the dashboard's state card shows the in-app
    // "Pay now" path by default. Override the key to exercise the external-portal fallback.
    [QTE_HANDLER_KEYS.taxProfile]: http.get('*/tax-profile/', () =>
        HttpResponse.json({
            state_of_residency: { id: 'CA', name: 'California' },
            state_filing_status: 'Single',
        })
    ),
    [QTE_HANDLER_KEYS.taxJurisdictionInfo]: http.get('*/qte/tax-jurisdiction-info/', () =>
        HttpResponse.json({
            jurisdiction: 'California',
            qte_payment_portal_url: 'https://www.ftb.ca.gov/pay',
            state_entity_tax_payment_portal_url: 'https://www.ftb.ca.gov/businesses',
            business_taxes: [],
        })
    ),
    // Inside an estimate window on both tabs, so the dashboard decides from the QTE as it does for most
    // of the year. Override the key with a `transition` phase (and turn the transition-state gate on)
    // for the between-quarters hero.
    [QTE_HANDLER_KEYS.displayState]: http.get('*/qte/display-state/', () =>
        HttpResponse.json({
            quarterly: { phase: 'estimate', tax_year: 2026, quarter: 3 },
            business: { phase: 'estimate', tax_year: 2026, quarter: 3 },
        } satisfies QTEDisplayState)
    ),
};
